import { YayApiResponse } from './api'
import { CallType, ConferenceCall } from './call'
import { Gift } from './gift'
import { Gender, User } from './user'

export type SubscriptionCadence = 'MONTHLY' | 'EVERY_SIX_MONTHS' | 'ANNUAL'
export type VipPlanType = 'annually' | 'bi-annually' | 'monthly'
export type PaymentMethodType = 'square' | 'slash'

export type Group = {
  _pinned?: boolean
  id: number
  topic: string
  description: string
  user_id: number
  groups_users_count: number
  posts_count: number
  gender: Gender
  place: string
  is_private: boolean
  group_category_id: number
  homepage: string
  secret: boolean
  only_verified_age: boolean
  only_mobile_verified: boolean
  // https://wiki.nanameue.jp/display/YAYAPP/Yay+1.3#Yay1.3-YAY-120Addcirclesettingthatuseragerange:morethan18,lessthan18orall.
  // 0: No restriction
  // 1: <= 17
  // 2: >= 18
  generation_groups_limit: 0 | 1 | 2
  views_count: number
  call_timeline_display: boolean
  safe_mode: boolean
  threads_count: number
  highlighted_count: number
  related_count: number
  sub_category_id: number
  allow_thread_creation_by: 'owner' | 'moderator' | 'member'
  allow_members_to_post_image_and_video: boolean
  allow_members_to_post_url: boolean
  title: 'admin' | 'official' | 'other'
  group_icon: string | null
  cover_image: string | null
  cover_image_thumbnail: string | null
  updated_at: number
  owner: {
    id: number
    username: string
    nickname: string
    prefecture: string
    biography: string
    followers_count: number
    followings_count: number
    gender: Gender
    is_private: boolean
    title: string
    posts_count: number
    groups_users_count: number
    reviews_count: number
    login_streak_count: number
    age_verified: boolean
    ticket: number
    // https://wiki.nanameue.jp/display/YAYAPP/Generation+group
    // 0: < 17
    // 1: 17
    // 2: 18
    // 3: > 18
    generation: 0 | 1 | 2 | 3
    country_code: string
    vip: boolean
    hide_vip: boolean
    is_online: boolean
    online_status: 'online' | 'hidden' | 'offline'
    profile_icon: string
    profile_icon_thumbnail: null
    cover_image: string
    cover_image_thumbnail: string
    last_loggedin_at: number
    created_at: number
    following: boolean
    followed_by: boolean
    follow_pending: boolean
    hidden: boolean
    mobile_number: null
    mobile_verified: boolean
    recently_kenta: boolean
    dangerous_user: boolean
    new_user: boolean
  }
  is_joined: boolean
  is_pending: boolean
  pending_count: number
  moderator_ids?: Array<number>
  pending_transfer_id: number | null
  pending_deputize_ids: number[] | null
  seizable: boolean
  seizable_before: null
  hide_reported_posts: boolean
  hide_conference_call: boolean
  hide_from_game_eight: boolean
  allow_ownership_transfer: boolean
  walkthrough_requested: boolean
  unread_counts?: number
  unread_threads_count: number
  guidelines: null | string
  invited_to_join: boolean
  joined_community_campaign?: boolean // Only when already joined the group.
}

export type GroupCreate = Partial<
  Group & {
    group_icon_filename: string
    cover_image_filename: string
    uuid: string
  }
>

export enum GroupRelationship {
  Joined = 'joined',
  Pending = 'pending',
  Unjoined = 'unjoined',
  Banned = 'banned',
}

export type PostType =
  | 'call'
  | 'image'
  | 'repost'
  | 'shareable_group'
  | 'shareable_url'
  | 'shareable_thread'
  | 'survey'
  | 'text'
  | 'thread'
  | 'video'
  | 'youtube'
  | 'timeline'

export type PostLocation = 'timeline' | 'group'

export type PostFeedback = 'good' | 'bad'
export type Feedback = {
  id: number
  post_id: number
  feedback: PostFeedback
  created_at: number
}

export type Post = {
  _pinned?: boolean
  _deleted?: boolean
  _isRootPost?: boolean
  _threadRelationship?: 'joined' | 'unjoined'
  _groupRelationship?: GroupRelationship
  _feedback?: PostFeedback
  _isRecommended?: number
  id: number
  text: string
  group_id: number
  font_size: number
  likes_count: number
  post_type: PostType | null
  highlighted: boolean
  color: number
  reposts_count: number
  created_at: number
  edited_at: null | number
  updated_at: number
  reported_count: number
  in_reply_to_post_count: number
  repostable: boolean
  in_reply_to?: number
  conversation_id?: number
  liked: boolean
  reposted: boolean
  tag: null
  total_gifts_count?: number
  group?: Group
  group_relationship?: GroupRelationship
  thread?: Thread
  thread_id?: number
  is_muted?: boolean
  videos?: any[]
  language?: 'en' | 'ja' | 'th' | 'id' | 'zh' | 'ko'

  gifts_count?: Array<{
    id: number
    quantity: number
  }>

  user: {
    id: number
    username: string
    nickname: string
    prefecture: string
    biography: string
    followers_count: number
    followings_count: number
    gender: Gender
    ticket: number
    is_private: boolean
    title: string
    posts_count: number
    groups_users_count: number
    reviews_count: number
    profile_icon: string
    cover_image: string
    profile_icon_thumbnail: string
    last_loggedin_at: number
    created_at: number
    login_streak_count: number
    is_online: boolean
    online_status: 'online' | 'hidden' | 'offline'
    country_code: 'JP' | string
    vip: boolean
    hide_vip: boolean
    following: boolean
    followed_by: boolean
    follow_pending: boolean
    hidden: boolean
    mobile_number: string | null
    mobile_verified: boolean
    age_verified: boolean
    recently_kenta: boolean
    dangerous_user: boolean
    generation: number
    new_user: boolean
    group_user?: {
      banned: boolean
      is_moderator: boolean
      pending_deputize: boolean
      pending_transfer: boolean
      title: string | null
    }
  }

  message_tags?: Array<{
    type: 'user' | 'all'
    length: number
    offset: number
    user_id?: number
  }>

  shareable?: {
    type?: string
    post?: {
      id: number
      text: string
      group_id: number
      font_size: number
      likes_count: number
      post_type: null
      color: number
      reposts_count: number
      created_at: number
      updated_at: number
      reported_count: number
      in_reply_to_post_count: number
      repostable: boolean
      conversation_id: number
      liked: boolean
      reposted: boolean
      user: User
      highlighted: boolean
      tag: null
      edited_at: null | number
    }
    group?: Partial<Group>
    ['posts/thread']?: Thread
  }
  in_reply_to_post?: {
    id: number
    text: string
    group_id: number
    color: number
    created_at: number
    user: User
  }
  mentions?: Array<{
    id: number
    nickname: string
    prefecture: string
    followers_count: number
    followings_count: number
    gender: Gender
    is_private: boolean
    title: string
    profile_icon: string
    profile_icon_thumbnail: string
    created_at: number
  }>
  shared_thread?: {
    id: number
    title: string
    unread_count: number
    created_at: number
    owner: {
      id: number
      username: string
      nickname: string
      profile_icon: string
      profile_icon_thumbnail: string
    }
    last_post: null | {
      id: number
      text: string
      post_type: null
      created_at: number
      user: {
        id: number
        username: string
        nickname: string
        profile_icon: string
        profile_icon_thumbnail: string
      }
    }
    thread_icon: string
  }
  conference_call?: ConferenceCall
  shared_url?: {
    id: number
    title: string
    description: string
    image_url: string
    url: string
    created_at: number
  }
  survey?: {
    id: number
    votes_count: number
    voted: true
    choices: Array<{
      id: number
      label: string
      votes_count: number
    }>
  }

  attachment?: string
  attachment_thumbnail?: string
  attachment_2?: string
  attachment_2_thumbnail?: string
  attachment_3?: string
  attachment_3_thumbnail?: string
  attachment_4?: string
  attachment_4_thumbnail?: string
  attachment_5?: string
  attachment_5_thumbnail?: string
  attachment_6?: string
  attachment_6_thumbnail?: string
  attachment_7?: string
  attachment_7_thumbnail?: string
  attachment_8?: string
  attachment_8_thumbnail?: string
  attachment_9?: string
  attachment_9_thumbnail?: string
}

export type PostRender = {
  post: Post
  isConversationLayout: boolean
  isConversationLayoutSmall?: boolean
  hasSeeMore?: boolean
}

export type PostCreate = {
  post_type: PostType
  text: string
  language?: string
  color: string
  font_size: string
  message_tags: string
  shared_url?: string
  attachment_sizes?: Record<string, [number, number]>
  call_type?: CallType
  in_reply_to?: string
  post_id?: string
  group_id?: string
  uuid?: string
}

export type PostCreated = YayApiResponse<Partial<Post>>

export type Thread = {
  created_at: number
  id: number
  is_joined: boolean
  last_post: null | {
    id: number
    text: string
    post_type: string
    created_at: number
    user: {
      id: number
      username: string
      nickname: string
      profile_icon: string
      profile_icon_thumbnail: string
    }
  }
  new_updates: boolean
  owner: {
    id: number
    username: string
    nickname: string
    profile_icon: string
    profile_icon_thumbnail: string
  }
  posts_count: number
  thread_icon: string
  title: string
  unread_count: number
  updated_at: number
}

export type GroupCategory = {
  id: number
  name: string
  icon: string
}

export type GroupMember = {
  is_moderator: boolean
  banned: boolean
  pending_transfer: boolean
  pending_deputize: boolean
  title: string
  user: {
    biography: string
    id: number
    nickname: string
    prefecture: string
    followers_count: number
    followings_count: number
    gender: Gender
    is_private: boolean
    title: string
    age_verified: boolean
    generation: number
    vip: boolean
    hide_vip: boolean
    is_online: boolean
    online_status: 'online' | 'hidden' | 'offline'
    profile_icon: string
    profile_icon_thumbnail: string
    created_at: number
    last_loggedin_at: number
    following: boolean
    followed_by: boolean
    follow_pending: boolean
    hidden: boolean
    recently_kenta: boolean
    dangerous_user: boolean
    new_user: boolean
  }
}

export type Review = {
  id: number
  comment: string
  reported_count: number
  user: Partial<User>
  reviewer: Partial<User>
  created_at: number
  mutual_review: boolean
}

export type FootPrint = {
  id: number
  user: User
  visited_at: number
}

export type Breadcrumb = {
  title: string
  link?: string
}

export type PaginationResult<T extends { id: string | number }> = {
  next_page_value: number
  list: Array<T>
}

export type PaginationQueryArg<T> = {
  from?: number
  number?: number
} & T

export type Walkthrough = {
  title: string
  url: string
}

export type Category = {
  id: number
  title: string
  icon_url: string
}

export type GameApp = Category & {
  platform_details?: {
    package_id: string
    affiliate_url?: string
  }
}

export type Notification = {
  id: string
  title?: string
  message: string
  type: 'error' | 'success'
  variant: 'snackbar' | 'alert' | 'dialog'
  duration: number
}

export type PromotionBanner = {
  id: number
  image_url: string
  promotion_url: string
  title: string
}

export type ImageLink = {
  id: number
  url: string
  link?: string
  title?: string
}

export type Word = {
  id: number
  type: string
  word: string
}

export type BooleanString = 'yes' | 'no'

export type RequestSNSToken = {
  request_secret: string
  request_token: string
  request_token_url: string
}

export type SNSSignIn = {
  req: {
    provider: string
    code: string
  }
  res: {
    access_token: string
    refresh_token: string
    token_type: string
    expires_in: number
    created_at: number
    sns_info: SNSInfo
    result: string
    is_new: boolean
    user_id: number
    username: null
    access_token_service: string
  }
}

export type SNSSignInError = {
  req: {
    provider: string
    access_token: string
  }
  res: {
    result: 'error'
    message: string
    error_code: number
    ban_until: null
  }
}

export type SNSType =
  | 'email'
  | 'line'
  | 'twitter'
  | 'apple'
  | 'facebook'
  | 'world_id'

export type SNSInfo = {
  type: SNSType
  uid: string
  nickname: string
  email: null
  biography: null
  profile_image: string
  gender: Gender
}

export type PhoneVerificationRequestsResponse = {
  phone_verification_request: PhoneVerificationRequest
  global_request: PhoneVerificationRequest
  result: string
}

export type PhoneVerificationRequest = {
  id: number
  status: string
  uuid: string
  verification_mode: string
  required: boolean
  info: null
  created_at: number
  updated_at: number
}

export type PresignedUrl = {
  result: string
  presigned_url: string
  expires_at: number
  method_type: string
}

export type UrlMetaData = {
  description: string
  image_url: string
  title: string
  url: string
}

export type CallBGM = {
  id: number
  title: string
  music_url: string
}

export type ChatRoom = {
  _pinned?: boolean
  id: number
  name: string
  icon: string
  icon_thumbnail: string
  background: string
  background_thumbnail: string
  updated_at: number
  unread_count: number
  user_setting: {
    notification_chat: boolean
  }
  is_group: boolean
  is_request: boolean
  last_message: {
    id: number
    text: string
    user_id: number
    font_size: number
    message_type: MessageType
    created_at: number
    attachment: string
    attachment_thumbnail: string
    attachment_android: string
    video_url: string
    video_thumbnail_url: string
    video_thumbnail_big_url: string
    video_processed: boolean
    reactions_count: any
    room_id: number
  }
  members: Array<User>
  owner: User
}

export type StickerPack = {
  id: number
  name: string
  description: string
  stickers: Array<Sticker>
  cover: string
}

export type Sticker = {
  id: number
  width: number
  height: number
  sticker_pack_id: number
  url: string
  extension: string
  position: string
}

export type GifCategory = {
  id: number
  name: string
  language: string
  gifs: Array<Gif>
}

export type Gif = {
  id: number
  width: number
  height: number
  url: string
}

export type MessageType =
  | 'text'
  | 'image'
  | 'eternal_image'
  | 'video'
  | 'eternal_video'
  | 'gif'
  | 'sticker'
  | 'screenshot_warning'
  | 'call'
  | 'individual_call'
  | 'owner_kick'
  | 'user_joined'
  | 'user_leave'
  | 'deleted'

export type Message = {
  _sending?: boolean
  id: number
  text: string
  user_id: number
  font_size: 0
  message_type: MessageType
  created_at: number
  attachment: null | string
  attachment_thumbnail: null | string
  attachment_android: null | string
  video_url: null | string
  video_thumbnail_url: null | string
  video_thumbnail_big_url: null | string
  video_processed: boolean
  reactions_count: {}
  room_id: number
  reacted: boolean
  sticker?: Sticker
  gif?: Gif
  conference_call?: ConferenceCall
  parent_id?: number
  parent?: Omit<Message, 'parent' | 'parent_id'>
  invitation?: Invitation
}

export type Invitation = {
  inviter: string
  first_user: string
  users_count: number
}

export type CreateMessage = {
  messageType: MessageType
  text?: string
  file?: File
  sticker_pack_id?: number
  sticker_id?: number
  sticker?: Sticker
  gif_image_id?: number
  gif?: Gif
  call_type?: null | 'vdo'
  parent_id?: number
}

export type UserInterests = {
  id: number
  name: string
  icon: string
  selected: boolean
}

export type AdditonalNotificationSetting = {
  notification_group_chat: boolean
  notification_private_chat: boolean
  notification_sound: string
  visible_on_sns_friend_recommendation: boolean
  notification_like: boolean
  notification_reply: boolean
  notification_follow: boolean
  notification_chat: boolean
  notification_follow_request: boolean
  notification_follow_accept: boolean
  notification_group_request: boolean
  notification_group_accept: boolean
  notification_group_delete: boolean
  notification_group_join: boolean
  notification_group_post: boolean
  notification_group_moderator: boolean
  notification_profile_screenshot: boolean
  notification_group_invite: boolean
  notification_follower_create_group: boolean
  notification_follower_conference_call: boolean
  notification_group_conference_call: boolean
  notification_call_invite: boolean
  notification_bulk_call_invite: boolean
  notification_review: boolean
  notification_daily_summary: boolean
  chat_request: boolean
  phone_on: boolean
  group_phone_on: boolean
  video_on: boolean
  group_video_on: boolean
  notification_repost: boolean
  notification_footprint: boolean
  notification_latest_news: boolean
  notification_popular_post: boolean
  notification_hima_now: boolean
  privacy_mode: boolean
  vip_invisible_footprint_mode: boolean
  chat_required_phone_verification: boolean
  invisible_on_user_search: boolean
  private_post: boolean
  age_restricted_on_review: boolean
  following_restricted_on_review: boolean
  notification_following_birthdate_on: boolean
  notification_birthday_to_followers: boolean
  private_user_timeline: boolean
  notification_chat_delete: boolean
  notification_contact_friend: boolean
  notification_twitter_friend: boolean
  hide_online_status: boolean
  notification_following_post_after_break: boolean
  notification_followings_in_call: boolean
  following_only_group_invite: boolean
  following_only_call_invite: boolean
  hide_active_call: boolean
  notification_security_warning: boolean
  caution_user_chat: boolean
  allow_reposts: boolean
  hide_vip: boolean
  hide_on_invitable: boolean
  notification_message_tag: boolean
  notification_group_message_tag_all: boolean
  hide_reply_public_timeline: boolean
  hide_reply_following_timeline: boolean
}

// Refer this wiki: https://wiki.nanameue.jp/display/YAYAPP/B2+Image+upload
export type Attachment = {
  file: File | Blob
  fileName: string
  originalFileName: string
  originalExtension: string
  naturalWidth: number
  naturalHeight: number
  isThumb: boolean
}

export type TwoFaSecret = {
  qr_code: string
  secret: string
}

export type CardDetails = {
  card_brand: string
  card_type: string
  id: string
  last_4: string
}

export interface getVipPlansResponseType {
  type: 'SUBSCRIPTION_TYPE'
  id: string
  updated_at: string
  created_at: string
  version: number
  is_deleted: boolean
  present_at_all_locations: boolean
  subscription_plan_data: SubscriptionPlanData
}

export interface SubscriptionPlanData {
  name: string
  subscription_plan_variations: Array<SubscriptionPlanVariation>
  eligible_item_ids: Array<string>
  all_items: boolean
}

export interface SubscriptionPlanVariation {
  type: string
  id: string
  updated_at: string
  created_at: string
  version: number
  is_deleted: boolean
  present_at_all_locations: boolean
  subscription_plan_variation_data: SubscriptionPlanVariationData
  discount?: number
  snowplow_type: VipPlanType
}

export interface SubscriptionPlanVariationData {
  name: string
  phases: Array<SubscriptionPhase>
  subscription_plan_id: string
}

export interface SubscriptionPhase {
  uid: string
  cadence: SubscriptionCadence
  ordinal: number
  pricing: RelativePricing
}

export interface SubscriptionPricing {
  type: 'STATIC' | 'RELATIVE'
}

export interface RelativePricing extends SubscriptionPricing {
  price: {
    amount: number
    currency: 'JPY' | 'USD'
  }
  price_money: {
    amount: number
    currency: 'JPY' | 'USD'
  }
}

export type LatestPlanResponse = {
  id: string
  location_id: string
  plan_variation_id: string
  customer_id: string
  start_date: string
  canceled_date?: string
  new_plan_variation_id?: string
  charged_through_date: string
  status: string
  invoice_ids: Array<string>
  version: number
  created_at: string
  card_id: string
  timezone: string
  source: {
    name: string
  }
  actions?: Array<any>
  monthly_billing_anchor_date: number
}

export interface Invoice {
  id: string
  version: number
  location_id: string
  order_id: string
  payment_requests: PaymentRequest[]
  primary_recipient: PrimaryRecipient
  invoice_number: string
  title: string
  public_url: string
  status: string
  timezone: string
  created_at: string
  updated_at: string
  accepted_payment_methods: AcceptedPaymentMethods
  delivery_method: string
  subscription_id: string
  store_payment_method_enabled: boolean
}

export interface PaymentRequest {
  uid: string
  request_type: string
  due_date: string
  tipping_enabled: boolean
  card_id: string
  computed_amount_money: ComputedAmountMoney
  total_completed_amount_money: TotalCompletedAmountMoney
  automatic_payment_source: string
}

export interface ComputedAmountMoney {
  amount: number
  currency: string
}

export interface TotalCompletedAmountMoney {
  amount: number
  currency: string
}

export interface PrimaryRecipient {
  customer_id: string
  given_name: string
  family_name: string
  email_address: string
}

export interface AcceptedPaymentMethods {
  card: boolean
  square_gift_card: boolean
  bank_account: boolean
  buy_now_pay_later: boolean
  cash_app_pay: boolean
}

export type WalletSource = 'dashboard' | 'app'

export type CurrencyRate = {
  JPY: string
  USD: string
}

export type TransactionType =
  | 'purchase.yay.gift'
  | 'purchase.yay.empl'
  | 'dev.add'
  | 'admin.add'
  | 'admin.deduct'
  | 'iap.purchase'
  | 'coin.convert'
  | 'gift.exchange'
  | 'bag.deposit'
  | 'bag.withdrawal'
  | 'bag.withdrawal_refund'
  | 'bag.withdrawal_to_yay'
  | 'bag.withdrawal_tokens'
  | 'pal.level_up'
  | 'purchase.pal_gacha'
  | 'pal.withdrawal_fee'
  | 'square.purchase'
  | 'slash.purchase'
  | 'bag.expired'
  | 'exchange.dotmoney'

export type EmplTransaction = {
  id: number
  transaction_code: any
  type: TransactionType
  occurred_at: number
  empl: Empl
  amount: string
  price?: number
  gift_transaction_uuid?: string
  gift_exchange_uuid?: string
  status?: string
  token_symbol?: string
  transaction_fee?: string
  money?: number
}

export interface Empl {
  regular: string
  rewarded: string
  locked: string
  unlocked: string
  total: string
}

export type StatusText = 'success' | 'pending' | 'fail'

export type WalletEmplTranasactionDetailsQueryResponse = {
  result: {
    id: number
    transaction_code: any
    transaction_fee?: string
    type: TransactionType
    occurred_at: number
    empl: Empl
    amount: string
    price?: number
    wallet_address: string
    status: StatusText
    money_amount?: number
  }
}

export type WalletTransactionDetailsQueryResponse = {
  result: {
    id: number
    status: StatusText
    transaction_code: string
    details: {
      yay_amount: string
      yay_max_output: string
      yay_min_output: string
      transaction_fee: number
    }
    amount: string
    wallet_address: string
    currency: string
    type: TransactionType
    description: string
    created_at: number
    updated_at: number
    final_status_updated_at: number
  }
}

export type ReceivedGiftTransactionItem = {
  item: {
    icon?: {
      url: string
      thumb: { url: string }
    }
    price: number
    position: number
    created_at: string
    updated_at: string
    is_active: boolean
    currency: string
    slug: string
    title: string
    description: any
  }
}

export type ReceivedGiftTransactionDetailsQueryResponse = {
  created_at: number
  sender: {
    username: string
    profile_icon_thumbnail?: string
  }
  receiver: {
    id: number
    username: any
    nickname: string
    profile_icon: string
    profile_icon_thumbnail: string
  }
  gifts: Array<ReceivedGiftTransactionItem>
}

export type GiftExchangeTransactionDetailsQueryResponse = {
  id: number
  rewarded_amount: number
  quantity: number
  status: string
  uuid: string
  created_at: number
  items: Array<{
    quantity: number
    gift: Gift
  }>
}

export type GiftExchangeTransactionHistoryQueryResponse = {
  gift_exchange_histories: GiftExchangeHistoryItem[]
  next_page_value: number
}

export interface GiftExchangeHistoryItem {
  id: number
  rewarded_amount: number
  quantity: number
  status: string
  uuid: string
  created_at: number
  items: Array<{
    quantity: number
    gift: Gift
  }>
}
